import { CustomFieldHint } from "@/components/custom-field/CustomFieldHint.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { JobProfileSelect } from "@/components/select/JobProfileSelect.tsx";
import { toNullableSalaryNumber } from "@/helpers/utils.helper.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  JobEditSystemFormData,
  useJobEditData,
  useJobEditForm,
} from "@/pages/recruit/jobs/edit/components/job-edit.type.ts";
import { JobEditTeamField } from "@/pages/recruit/jobs/edit/components/JobEditTeamField.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useJobsApprovalPolicies } from "@/queries/recruit/use-jobs.query.ts";
import { usePipelines } from "@/queries/recruit/use-pipelines.query.ts";
import { useDepartmentsTree } from "@/queries/use-departments.query.ts";
import { useDivisionsByIds, useDivisionsSearch } from "@/queries/use-divisitions.query.ts";
import { useLocations } from "@/queries/use-locations.query.ts";
import { ApiEntityTypes } from "@/types/common.types.ts";
import { ApiField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiDivisionsFilterDefault } from "@/types/division.types.ts";
import {
  getJobPriorityColorClasses,
  getJobPriorityTitle,
  JobPriority,
} from "@/types/recruit/job.types.ts";
import { Box, Input, Select, SelectItem, Stack, Tag, Text, Title } from "@skailer-com/uikit";
import { ReactNode, useMemo, useState } from "react";
import { Controller, useController, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

type JobTextFieldName = Extract<
  keyof JobEditSystemFormData,
  "title" | "public_title" | "description" | "requirements" | "responsibilities" | "conditions"
>;

const textFieldNames: Partial<Record<ApiFieldTypes, JobTextFieldName>> = {
  [ApiFieldTypes.job_title]: "title",
  [ApiFieldTypes.job_public_title]: "public_title",
};

const richTextPlaceholders: Partial<Record<JobTextFieldName, string>> = {
  description: "common:description_enter_using_editor",
  requirements: "hiring_plan:enter_requirements",
  responsibilities: "hiring_plan:enter_responsibilities",
  conditions: "hiring_plan:enter_conditions",
};

const richTextFieldNames: Partial<Record<ApiFieldTypes, JobTextFieldName>> = {
  [ApiFieldTypes.job_description]: "description",
  [ApiFieldTypes.job_requirements]: "requirements",
  [ApiFieldTypes.job_responsibilities]: "responsibilities",
  [ApiFieldTypes.job_conditions]: "conditions",
};

type JobFieldProps = { label: string; required: boolean; placeholder?: string };

export function JobEditField({ field }: { field: ApiField }) {
  const { getFieldTitle, getFieldByType } = useCustomFieldsHook();
  const formHook = useJobEditForm();
  const { job } = useJobEditData();
  const props: JobFieldProps = {
    label: getFieldTitle(field),
    required: field.is_mandatory,
    placeholder: field.description.trim() || undefined,
  };

  if (field.type === ApiFieldTypes.custom) {
    return getFieldByType({
      field,
      formHook,
      canView: true,
      canEdit: true,
      fieldValue:
        job?.custom_fields?.find((item) => item.field.id === field.id)?.value ?? undefined,
    });
  }

  return <CustomFieldHint hint={field.hint}>{getSystemField(field, props)}</CustomFieldHint>;
}

function getSystemField(field: ApiField, props: JobFieldProps): ReactNode {
  const textName = textFieldNames[field.type];
  const richTextName = richTextFieldNames[field.type];

  if (textName) return <JobTextField name={textName} {...props} />;
  if (richTextName) return <JobRichTextField name={richTextName} {...props} />;

  switch (field.type) {
    case ApiFieldTypes.job_department:
      return <JobDepartmentField {...props} />;
    case ApiFieldTypes.job_division:
      return <JobDivisionField {...props} />;
    case ApiFieldTypes.job_job_profile:
      return <JobProfileField {...props} />;
    case ApiFieldTypes.job_location:
      return <JobLocationField {...props} />;
    case ApiFieldTypes.job_priority:
      return <JobPriorityField {...props} />;
    case ApiFieldTypes.job_pipeline:
      return <JobPipelineField {...props} />;
    case ApiFieldTypes.job_approval_policy:
      return <JobApprovalPolicyField {...props} />;
    case ApiFieldTypes.job_recruiters:
      return <JobEditTeamField kind="recruiters" {...props} />;
    case ApiFieldTypes.job_stakeholders:
      return <JobEditTeamField kind="stakeholders" {...props} />;
    case ApiFieldTypes.job_salary_min:
      return <JobSalaryField name="salary_min" {...props} />;
    case ApiFieldTypes.job_salary_max:
      return <JobSalaryField name="salary_max" {...props} />;
    case ApiFieldTypes.job_currency:
      return <JobCurrencyField {...props} />;
    case ApiFieldTypes.job_files:
      return <JobFilesField {...props} />;
    default:
      return null;
  }
}

function JobTextField({
  name,
  label,
  required,
  placeholder,
}: JobFieldProps & { name: JobTextFieldName }) {
  const {
    register,
    formState: { errors },
  } = useJobEditForm();
  return (
    <Input
      label={label}
      required={required}
      placeholder={placeholder}
      type={"text"}
      {...register(name)}
      error={errors[name]?.message}
    />
  );
}

function JobRichTextField({
  name,
  label,
  required,
  placeholder,
}: JobFieldProps & { name: JobTextFieldName }) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  const defaultPlaceholder = richTextPlaceholders[name];
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <Stack>
          <Title header={label} size={"sm"} paddingBottom paddingTop required={required} />
          <RichEditor
            value={value || ""}
            onChange={onChange}
            placeholder={placeholder ?? (defaultPlaceholder ? t(defaultPlaceholder) : undefined)}
            error={errors[name]?.message}
          />
        </Stack>
      )}
    />
  );
}

function JobPriorityField({ label, required }: JobFieldProps) {
  const JOB_PRIORITY_NOT_DEFINED_ID = "not_defined";
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  const options: SelectItem[] = useMemo(
    () => [
      {
        id: JOB_PRIORITY_NOT_DEFINED_ID,
        title: t("ats:job_priority_not_defined"),
        icon: "fa-solid fa-circle text-dark/30",
      },
      ...[JobPriority.low, JobPriority.medium, JobPriority.high].map((priority) => ({
        id: priority,
        title: getJobPriorityTitle(priority),
        icon: `fa-solid fa-circle ${getJobPriorityColorClasses(priority)?.dotTextClass ?? ""}`,
      })),
    ],
    [t]
  );
  return (
    <Controller
      control={control}
      name={"priority"}
      render={({ field: { value, onChange } }) => (
        <Select
          label={label}
          required={required}
          options={options}
          emptyTitle={t("ats:job_priority_not_defined")}
          value={value ?? JOB_PRIORITY_NOT_DEFINED_ID}
          onChange={(priority) => {
            onChange(priority === JOB_PRIORITY_NOT_DEFINED_ID ? null : priority);
          }}
          error={errors.priority?.message}
        />
      )}
    />
  );
}

function JobDivisionField({ label, required, placeholder }: JobFieldProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  const [divisionsFilter, setDivisionsFilter] = useState(ApiDivisionsFilterDefault);
  const { data: divisions, isLoading } = useDivisionsSearch(divisionsFilter);
  const selectedDivisionId = useWatch({ control, name: "division_id" });
  const { data: selectedDivisionItems } = useDivisionsByIds({
    ids: selectedDivisionId ? [selectedDivisionId] : [],
    enabled: !!selectedDivisionId,
  });
  const options = useMemo<SelectItem[]>(() => {
    const searchItems = divisions?.items ?? [];
    const selectedItem = selectedDivisionItems?.[0];
    if (!selectedItem) return searchItems;
    if (searchItems.some((item) => item.id === selectedItem.id)) return searchItems;
    return [selectedItem, ...searchItems];
  }, [divisions?.items, selectedDivisionItems]);
  return (
    <Controller
      control={control}
      name={"division_id"}
      render={({ field: { value, onChange } }) => (
        <Select
          label={label}
          required={required}
          options={options}
          isLoading={isLoading}
          searchable
          isClearable
          onSearchOptions={(text) => {
            setDivisionsFilter({ ...divisionsFilter, text });
          }}
          emptyTitle={placeholder ?? t("common:select")}
          searchOptions={options}
          value={value}
          onChange={onChange}
          error={errors.division_id?.message}
        />
      )}
    />
  );
}

function JobDepartmentField({ label, required, placeholder }: JobFieldProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  const { data: departments, isLoading } = useDepartmentsTree();
  return (
    <Controller
      control={control}
      name={"department_id"}
      render={({ field: { value, onChange } }) => (
        <Select
          label={label}
          required={required}
          searchable
          isClearable
          options={departments}
          isLoading={isLoading}
          emptyTitle={placeholder ?? t("common:select")}
          value={value}
          onChange={onChange}
          error={errors.department_id?.message}
        />
      )}
    />
  );
}

function JobProfileField({ label, required, placeholder }: JobFieldProps) {
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  return (
    <Controller
      control={control}
      name={"job_profile_id"}
      render={({ field: { value, onChange } }) => (
        <JobProfileSelect
          label={label}
          required={required}
          emptyTitle={placeholder}
          isClearable
          value={value}
          onChange={onChange}
          error={errors.job_profile_id?.message}
        />
      )}
    />
  );
}

function JobLocationField({ label, required, placeholder }: JobFieldProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  const { data: locations, isLoading } = useLocations();
  return (
    <Controller
      control={control}
      name={"location_id"}
      render={({ field: { value, onChange } }) => (
        <Select
          label={label}
          required={required}
          options={locations || []}
          isClearable
          isLoading={isLoading}
          searchable
          emptyTitle={placeholder ?? t("common:select")}
          value={value}
          onChange={onChange}
          error={errors.location_id?.message}
        />
      )}
    />
  );
}

function JobCurrencyField({ label, required, placeholder }: JobFieldProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  return (
    <Controller
      control={control}
      name={"currency_code"}
      render={({ field: { value, onChange } }) => (
        <CurrencySelect
          label={label}
          required={required}
          emptyTitle={placeholder ?? t("common:select")}
          value={value}
          searchable
          onChange={onChange}
          error={errors.currency_code?.message}
        />
      )}
    />
  );
}

function JobSalaryField({
  name,
  label,
  required,
  placeholder,
}: JobFieldProps & { name: "salary_min" | "salary_max" }) {
  const {
    register,
    formState: { errors },
  } = useJobEditForm();
  return (
    <Input
      label={label}
      required={required}
      placeholder={placeholder}
      type={"number"}
      {...register(name, { setValueAs: toNullableSalaryNumber })}
      error={errors[name]?.message}
    />
  );
}

function JobPipelineField({ label, required, placeholder }: JobFieldProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  const { data: pipelines, isLoading } = usePipelines();
  const selectedPipelineId = useWatch({ control, name: "pipeline_id" });
  const selectedPipeline = pipelines?.find((x) => x.id == selectedPipelineId);
  return (
    <Stack gap={"sm"}>
      <Controller
        control={control}
        name={"pipeline_id"}
        render={({ field: { value, onChange } }) => (
          <Select
            required={required}
            label={label}
            options={pipelines}
            emptyTitle={placeholder ?? t("common:select")}
            isLoading={isLoading}
            value={value}
            onChange={onChange}
            error={errors.pipeline_id?.message}
          />
        )}
      />
      {selectedPipeline && selectedPipeline.stages.length > 0 && (
        <Stack direction={"horizontal"} gap={"sm"} className={"flex-wrap pb-4"}>
          {selectedPipeline.stages.map((stage) => (
            <Tag key={`job-stage-${stage.id}`} variant={"dark"}>
              {stage.title}
            </Tag>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function JobApprovalPolicyField({ label, required, placeholder }: JobFieldProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useJobEditForm();
  const { data: policies, isLoading } = useJobsApprovalPolicies(true);
  const options = useMemo<SelectItem[]>(
    () => (policies ?? []).map((x) => ({ id: x.id, title: x.title })),
    [policies]
  );
  return (
    <Controller
      control={control}
      name={"approval_policy_id"}
      render={({ field: { value, onChange } }) => (
        <Select
          isClearable
          label={label}
          required={required}
          options={options}
          isLoading={isLoading}
          emptyTitle={placeholder ?? t("common:select")}
          searchOptions={options}
          value={value}
          onChange={onChange}
          error={errors.approval_policy_id?.message}
        />
      )}
    />
  );
}

function JobFilesField({ label, required }: JobFieldProps) {
  const { t } = useTranslation();
  const { control } = useJobEditForm();
  const { removeFile } = useJobEditData();
  const {
    field: { value: files, onChange },
    fieldState: { error },
  } = useController({ control, name: "files" });
  const { data: employeeMe } = useEmployeeMe();
  return (
    <Stack gap={"md"}>
      <Title header={label} size={"sm"} paddingTop required={required} />
      {files.length > 0 && (
        <Stack gap={"sm"}>
          {files.map((file) => (
            <FileItem key={`file${file.id}`} file={file} onRemove={() => removeFile(file)} />
          ))}
        </Stack>
      )}
      <Stack items={"start"}>
        <Box>
          <FileUpload
            entityId={employeeMe?.id}
            entityType={ApiEntityTypes.employee}
            afterSend={(file) => {
              if (file) onChange([...files, file]);
            }}
            buttonText={t("common:attach")}
          />
        </Box>
      </Stack>
      {error?.message && <Text className="text-danger">{error.message}</Text>}
    </Stack>
  );
}
